export type AuthUser = {
  uid: number;
  nickname: string;
  avatar?: string;
  hasFilledSurvey?: boolean;
  language?: string;
  isCompGuideVisible?: boolean;
  isCombGuideVisible?: boolean;
  isAutoGuideVisible?: boolean;
};

export type AuthSession = {
  accessToken: string;
  refreshToken: string;
  accessExpiresAt: number;
  refreshExpiresAt: number;
  user: AuthUser;
  deviceId: string;
};

const AUTH_STORAGE_KEY = "pingpi.teacherWeb.auth.v1";
const DEVICE_STORAGE_KEY = "pingpi.teacherWeb.deviceId.v1";
const EXPIRY_BUFFER_MS = 5 * 60 * 1000;

function safeRead(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeWrite(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Storage can be unavailable in some embedded browsers. Login still works for the current page load.
  }
}

function safeRemove(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Ignore storage cleanup failures in restricted WebViews.
  }
}

function randomId(): string {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  if (window.crypto?.getRandomValues) {
    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  }

  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

export function getDeviceId(): string {
  const stored = safeRead(DEVICE_STORAGE_KEY);
  if (stored) {
    return stored;
  }

  const deviceId = `teacher-web-${randomId()}`;
  safeWrite(DEVICE_STORAGE_KEY, deviceId);
  return deviceId;
}

export function loadSession(): AuthSession | null {
  const raw = safeRead(AUTH_STORAGE_KEY);
  if (!raw) {
    return null;
  }

  try {
    const session = JSON.parse(raw) as AuthSession;
    if (!session.accessToken || !session.refreshToken || !session.user?.uid) {
      clearSession();
      return null;
    }
    return session;
  } catch {
    clearSession();
    return null;
  }
}

export function saveSession(session: AuthSession): void {
  safeWrite(AUTH_STORAGE_KEY, JSON.stringify(session));
}

export function clearSession(): void {
  safeRemove(AUTH_STORAGE_KEY);
}

export function isAccessValid(session: AuthSession): boolean {
  return session.accessExpiresAt - EXPIRY_BUFFER_MS > Date.now();
}

export function isRefreshValid(session: AuthSession): boolean {
  return session.refreshExpiresAt > Date.now();
}

export function makeSession(input: {
  accessToken: string;
  expiresIn: number;
  refreshToken: string;
  refreshExpiresIn: number;
  user: AuthUser;
  deviceId?: string;
}): AuthSession {
  const now = Date.now();
  return {
    accessToken: input.accessToken,
    refreshToken: input.refreshToken,
    accessExpiresAt: now + input.expiresIn * 1000,
    refreshExpiresAt: now + input.refreshExpiresIn * 1000,
    user: input.user,
    deviceId: input.deviceId ?? getDeviceId(),
  };
}
